import { resolveConsumerRuntimeConnectionUuid, resolveConsumerRuntimeEventsBase, resolveSellingPointId } from "./runtime-endpoints";

const LAST_EVENT_AT_KEY = "consumerRuntimeLastEventAt";

type RuntimeEvent = {
  eventId?: string;
  eventType?: string;
  createdAt?: string;
  payload?: Record<string, unknown>;
};

const toWsUrl = (baseUrl: string) => {
  if (!baseUrl) return "";
  if (baseUrl.startsWith("https://")) return `wss://${baseUrl.slice("https://".length)}/`;
  if (baseUrl.startsWith("http://")) return `ws://${baseUrl.slice("http://".length)}/`;
  return "";
};

const readLastEventAt = () => {
  try {
    return localStorage.getItem(LAST_EVENT_AT_KEY) || "";
  } catch {
    return "";
  }
};

const writeLastEventAt = (value?: string) => {
  if (!value) return;
  try {
    localStorage.setItem(LAST_EVENT_AT_KEY, value);
  } catch {
    // ignore storage errors
  }
};

export function connectConsumerRuntimeEvents(onRefresh: (reason?: string) => void) {
  const connectionUuid = resolveConsumerRuntimeConnectionUuid();
  const sellingPointId = resolveSellingPointId();
  const baseUrl = resolveConsumerRuntimeEventsBase(connectionUuid);
  const wsUrl = toWsUrl(baseUrl);

  if (!connectionUuid || !sellingPointId || !wsUrl || typeof WebSocket === "undefined") {
    return () => {};
  }

  let socket: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;

  const send = (payload: Record<string, unknown>) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify(payload));
  };

  const ack = (event: RuntimeEvent) => {
    if (!event.eventId) return;
    send({
      type: "ack",
      eventId: event.eventId,
      seenAt: new Date().toISOString(),
    });
  };

  const handleEvent = (event: RuntimeEvent) => {
    if (event.createdAt) writeLastEventAt(event.createdAt);
    ack(event);
    if (event.eventType === "runtime.refresh") {
      onRefresh(String(event.payload?.reason || ""));
    }
    window.dispatchEvent(new CustomEvent("runtime:event", { detail: event }));
  };

  const scheduleReconnect = () => {
    if (closed || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      open();
    }, 2500);
  };

  const open = () => {
    socket = new WebSocket(wsUrl);

    socket.addEventListener("open", () => {
      send({
        type: "hello",
        terminalId: connectionUuid,
        sellingPointId,
        roles: ["kiosk"],
      });
    });

    socket.addEventListener("message", (raw) => {
      try {
        const message = JSON.parse(String(raw.data || "{}")) as {
          type?: string;
          data?: any;
        };
        const type = String(message.type || "").trim().toLowerCase();
        if (type === "hello_ack") {
          send({
            type: "subscribe",
            since: readLastEventAt() || undefined,
            limit: 200,
          });
          return;
        }
        if (type === "event") {
          handleEvent(message.data || {});
          return;
        }
        if (type === "event_batch") {
          const events = Array.isArray(message.data?.events) ? message.data.events : [];
          events.forEach((entry: RuntimeEvent) => handleEvent(entry || {}));
        }
      } catch {
        // ignore malformed event frames
      }
    });

    socket.addEventListener("close", () => {
      socket = null;
      scheduleReconnect();
    });

    socket.addEventListener("error", () => {
      if (!socket) return;
      // In dev + React Strict Mode the first effect instance can be torn down
      // before the initial handshake completes. Avoid noisy console errors from
      // trying to close an already-closing socket in CONNECTING state.
      if (socket.readyState === WebSocket.CONNECTING) return;
      socket.close();
    });
  };

  open();

  return () => {
    closed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    if (socket && socket.readyState === WebSocket.OPEN) socket.close();
    socket = null;
  };
}
